// Why a Google sign-in did not finish, in one place (#2457).
//
// Every surface that starts a sign-in (the landing CTA, /pricing, the claim
// and feedback pages, the return from a redirect) reads its failure reason,
// its on-screen copy and its analytics `reason` from HERE, so the Desk's
// sign-in health read and what the student sees can never disagree.
//
// The evidence behind each branch (PostHog, 2026-07-01 to 2026-09-29):
//   - iOS Safari completed 12 of 16 sign-ins while mobile used the redirect
//     (to Jul 29), then 22 of 56 once sign-in went popup-first. In 29 of the
//     popup-era iOS starts the opener page fired $pageleave within 30s and
//     never heard back: the popup tab took the page down with it.
//   - Every LinkedIn in-app browser start since Aug 9 (5 of 5 people) went
//     popup-blocked -> redirect -> auth/network-request-failed. Google
//     sign-in does not work inside that webview.
//   - Every Windows sign-in failure in the 30 days to Oct 8 (13 people, #3125)
//     was a browser that could not reach Google at all: Google's sign-in
//     script failed in ~60ms (auth/internal-error), then the redirect's first
//     call to Google failed in ~100ms (auth/network-request-failed). Blocking
//     apis.google.com AND identitytoolkit.googleapis.com in a test browser
//     reproduces it exactly; blocking only the script does not (the redirect
//     then works). Those were datacenter browsers, but a school network or a
//     privacy extension that blocks Google looks the same to the student, so
//     it gets its own reason and its own words (google_unreachable).
//   - Every Mac "lost" popup since the marker shipped (7 events, 5 people,
//     all Chrome 153-154, Sep 30 to Oct 8, #3483) was the page that opened
//     Google's window leaving while that window was still open: a reload or
//     Back 2-4s after the click (6 of 7; one after 6 minutes), when the
//     fastest real Mac Chrome popup sign-in takes 4s (median 5s, 36 of 47).
//     One was not lost at all: the student went Back inside the app and the
//     notice fired on that route change while Google's window was still up
//     (it reported popup-closed-by-user 6s later). A page that reloads
//     orphans the window: a sign-in finished there reaches nobody.
//   - Every iOS "redirect_lost" since the load type was recorded (2 people,
//     Oct 8, #3894) loaded as back_forward 5s and 18s after the tap: the
//     student pressed Back on Google's account screen. Firebase's handler
//     returns with location.replace, a plain navigation, so a real return
//     never loads as back_forward. Reproduced in WebKit (iPhone profile)
//     against production: Start free, Google's page, Back -> the app told
//     the student the sign-in "didn't finish" and to try another browser.
//     Going Back is the student's choice, like closing the popup.
//   - Every other iOS Safari "redirect_lost" (Oct 9 to 13, #4117) was not a
//     Firebase return at all. 2c6ecb1c started on /colleges/stanford-university
//     and came back 17s later by re-opening the text message's link
//     (/colleges?utm_source=sms..., a plain navigate load); Firebase's handler
//     returns to the exact page the sign-in started on. In the same days no
//     iOS return to the start page came back empty, no
//     auth/missing-initial-state was recorded, and 3 of 3 Safari students who
//     finished on Google's page were signed in. WebKit with the iPhone profile
//     against production serves /__/auth/handler, /__/auth/iframe and
//     init.json from this site with no CSP refusal. The redirect works; the
//     students left Google's page (for a Safari with no Google session it is
//     Google's full email and password form). A return to ANOTHER page is the
//     student coming back their own way: its own reason, never "lost", and
//     the email link is the way in that is offered.

// A reason is the analytics dimension AND the copy key. Keep the set small:
// the Desk groups signin_failed by it, per device.
export const REASONS = Object.freeze({
    NETWORK: 'network',
    POPUP_BLOCKED: 'popup_blocked',
    POPUP_CLOSED: 'popup_closed',
    POPUP_LOST: 'popup_lost',
    REDIRECT_LOST: 'redirect_lost',
    REDIRECT_BACK: 'redirect_back',
    REDIRECT_LEFT: 'redirect_left',
    IN_APP_BROWSER: 'in_app_browser',
    BROWSER_UNSUPPORTED: 'browser_unsupported',
    GOOGLE_UNREACHABLE: 'google_unreachable',
    // #5154: the address already has an account that signs in another way;
    // and the password guard's two refusals of a provider sign-in.
    ACCOUNT_EXISTS: 'account_exists',
    NO_EMAIL: 'no_email',
    ADDRESS_MISMATCH: 'address_mismatch',
    OTHER: 'other',
});

// The password guard's refusal words (cloud_functions/auth_guard/
// password_guard.py, pinned equal by a drift test). Firebase hands a
// blocking function's refusal back inside the error's message, so the words
// are how the page knows which refusal it was.
export const GUARD_NO_EMAIL_MESSAGE = 'Your Facebook account has no email on it. Use Google, Apple or your email.';
export const GUARD_MISMATCH_MARK = "account does not match the account's email";

/** 'no_email' | 'address_mismatch' | null for a sign-in error. */
export function guardRefusal(error) {
    const msg = String(error?.message || '');
    if (msg.includes(GUARD_NO_EMAIL_MESSAGE)) return REASONS.NO_EMAIL;
    if (msg.includes(GUARD_MISMATCH_MARK)) return REASONS.ADDRESS_MISMATCH;
    return null;
}

// Codes Stratia mints itself (never Firebase's) for the two failures Firebase
// cannot see: a sign-in whose page died mid-flight, and a webview we refuse to
// start one in.
export const LOST_CODE = 'stratia/sign-in-lost';
export const IN_APP_CODE = 'stratia/in-app-browser';
// The student went Back from Google's page before choosing an account
// (#3894). Recorded cancelled, like a closed popup, never as lost.
export const BACK_CODE = 'stratia/sign-in-back';
// The student came back to the site on another page than the one the
// redirect started from (#4117): a re-opened link, a typed address. Firebase
// only ever returns to the start page, so this one never reached it.
// Recorded cancelled, like Back.
export const LEFT_CODE = 'stratia/sign-in-left';
// The student asked to finish in this tab because Google's window was not in
// sight (#3483). Recorded on signin_popup_fallback, like any other switch.
export const POPUP_UNSEEN_CODE = 'stratia/popup-unseen';

// How long the overlay waits before offering "Sign in in this tab": half the
// fastest real popup sign-in (4s on Mac Chrome, #3483), so it is on screen
// before the 2-4s window in which students reloaded or went Back.
export const POPUP_HELP_AFTER_MS = 2000;

export const CANCELLED_CODES = Object.freeze([
    'auth/popup-closed-by-user', 'auth/cancelled-popup-request', 'auth/user-cancelled',
]);

export function failureReason(code, error = null) {
    const refused = error ? guardRefusal(error) : null;
    if (refused) return refused;
    switch (code) {
        case 'auth/account-exists-with-different-credential':
            return REASONS.ACCOUNT_EXISTS;
        case 'auth/network-request-failed':
        case 'auth/timeout':
            return REASONS.NETWORK;
        case 'auth/popup-blocked':
            return REASONS.POPUP_BLOCKED;
        case 'auth/popup-closed-by-user':
        case 'auth/cancelled-popup-request':
        case 'auth/user-cancelled':
            return REASONS.POPUP_CLOSED;
        case 'auth/missing-initial-state':
            return REASONS.REDIRECT_LOST;
        case IN_APP_CODE:
            return REASONS.IN_APP_BROWSER;
        case BACK_CODE:
            return REASONS.REDIRECT_BACK;
        case LEFT_CODE:
            return REASONS.REDIRECT_LEFT;
        case 'auth/internal-error':
        case 'auth/operation-not-supported-in-this-environment':
        case 'auth/web-storage-unsupported':
            return REASONS.BROWSER_UNSUPPORTED;
        default:
            return REASONS.OTHER;
    }
}

// In-app browsers whose webview Google sign-in fails in. Matched on the
// marker each app appends to the user agent. The Google app (GSA) is NOT
// here: Google signs its own users in, and GSA completed sign-ins in the
// same data.
const IN_APP_MARKERS = [
    ['LinkedIn', /LinkedInApp/i],
    ['Instagram', /Instagram/i],
    ['Facebook', /FBAN|FBAV|FB_IAB/i],
    ['TikTok', /musical_ly|BytedanceWebview|TikTok/i],
    ['Snapchat', /Snapchat/i],
    ['Pinterest', /Pinterest/i],
    ['X', /Twitter/i],
    ['LINE', /\bLine\//],
];

export function inAppBrowserName(ua = (typeof navigator !== 'undefined' ? navigator.userAgent : '') || '') {
    for (const [name, re] of IN_APP_MARKERS) if (re.test(ua)) return name;
    return null;
}

// Every iOS browser is WebKit. iPadOS 13+ Safari asks for the desktop site by
// default and presents as a Mac; touch points are the tell (reviewer, #2034).
export function isIOS() {
    const ua = (typeof navigator !== 'undefined' && navigator.userAgent) || '';
    return /iPhone|iPad|iPod/i.test(ua)
        || (/Macintosh/i.test(ua) && ((typeof navigator !== 'undefined' && navigator.maxTouchPoints) || 0) > 1);
}

// Which sheet a browser gets (#5084, founder pick A): 'in_app' (any app's
// browser above), 'ios_safari' (iOS WebKit that is Safari: iPhone, iPad and
// the Home Screen app, which is Safari underneath; founder 2026-10-21: all
// three lead with email) or 'other'. Chrome, Firefox and Edge on iOS mark
// themselves and finished their Google sign-ins (5 of 5 for Chrome), and the
// Google app signs its own users in: they keep the Google-first sheet. The
// one function the mode AND the analytics `browser_class` read, so the
// screen and the funnel can never disagree. The sheet never sniffs the user
// agent itself.
const IOS_OTHER_BROWSERS = /CriOS|FxiOS|EdgiOS|GSA\//;
export const BROWSER_CLASSES = Object.freeze(['ios_safari', 'in_app', 'other']);

export function signInBrowserClass(
    ua = (typeof navigator !== 'undefined' ? navigator.userAgent : '') || '',
    touchPoints = (typeof navigator !== 'undefined' && navigator.maxTouchPoints) || 0,
) {
    if (inAppBrowserName(ua)) return 'in_app';
    const ios = /iPhone|iPad|iPod/i.test(ua) || (/Macintosh/i.test(ua) && touchPoints > 1);
    return ios && !IOS_OTHER_BROWSERS.test(ua) ? 'ios_safari' : 'other';
}

// Which platform a sign-in sheet is drawn for (#5154, founder pick A,
// 2026-10-22): the provider order is a table keyed by it
// (signInProviders.PROVIDER_ORDER), and the analytics `platform` reads the
// same function, so the screen and the funnel cannot disagree. Read from the
// user agent at render time only: no client-hint request, no stored device
// id. iPadOS asks for the desktop site and presents as a Mac; touch points
// are the tell, as isIOS() reads them.
export const PLATFORMS = Object.freeze(['ios', 'android', 'desktop', 'unknown']);

export function signInPlatform(
    ua = (typeof navigator !== 'undefined' ? navigator.userAgent : '') || '',
    touchPoints = (typeof navigator !== 'undefined' && navigator.maxTouchPoints) || 0,
) {
    if (/iPhone|iPad|iPod/i.test(ua) || (/Macintosh/i.test(ua) && touchPoints > 1)) return 'ios';
    if (/Android/i.test(ua)) return 'android';
    if (/Windows|Macintosh|CrOS|Linux|X11/i.test(ua)) return 'desktop';
    return 'unknown';
}

// ── the attempt id (#5084) ──────────────────────────────────────────────────
// One random id per sign-in attempt, made when the sheet opens, so the
// funnel joins sheet opened -> started -> link sent -> completed, even when
// the link finishes in another browser (it rides the link's carry,
// emailLinkSignIn.urlCarry). Random, never derived from the address.
// sessionStorage, beside the in-flight marker: it survives the hop to Google
// and back in this tab, and a second tab is a second attempt.
const ATTEMPT_KEY = 'stratia_signin_attempt';
export const ATTEMPT_RE = /^[a-z0-9]{12,32}$/;

export function newSignInAttempt() {
    let id = '';
    try {
        const bytes = new Uint8Array(10);
        crypto.getRandomValues(bytes);
        id = [...bytes].map((b) => b.toString(36).padStart(2, '0')).join('');
    } catch {
        id = `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 10)}`;
    }
    try { sessionStorage.setItem(ATTEMPT_KEY, id); } catch { /* storage off: this page life only */ }
    return id;
}

export function signInAttemptId() {
    try {
        const id = sessionStorage.getItem(ATTEMPT_KEY);
        return id && ATTEMPT_RE.test(id) ? id : null;
    } catch { return null; }
}

/** A finished sign-in ends its attempt: a later one in this tab gets its own. */
export function endSignInAttempt() {
    try { sessionStorage.removeItem(ATTEMPT_KEY); } catch { /* storage off */ }
}

/** What every signin_* event adds (#5084): the browser class and, when one
 *  is open, the attempt id. `attempt`: an id the caller read elsewhere (a
 *  link finished in another browser). */
export function signInAttemptProps(attempt = signInAttemptId()) {
    return { browser_class: signInBrowserClass(), ...(attempt && ATTEMPT_RE.test(attempt) ? { attempt_id: attempt } : {}) };
}

// ── the in-flight marker ────────────────────────────────────────────────────
// Set when a sign-in starts, cleared when it resolves either way. A marker
// still present on the NEXT page load, with nobody signed in, is a sign-in
// that died mid-flight (the popup took the page down, or the redirect came
// back without its result): the one failure no Firebase error ever reports.
// sessionStorage, not localStorage: per tab, so a second open tab can never
// read another tab's in-flight popup as lost; and it survives both a reload
// and the same-tab hop to Google and back.
const PENDING_KEY = 'stratia_signin_pending';
const PENDING_TTL_MS = 15 * 60 * 1000;

// This page life. A marker written by THIS document is a sign-in still in
// flight, never a lost one, however often the reader runs (#3483: a route
// change re-ran it while Google's window was open and told the student the
// sign-in had failed). sessionStorage survives a reload and the hop to
// Google; this does not, which is exactly the line between the two.
const PAGE_LIFE = `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;

// The page a sign-in started on, as a path (#4117). Firebase's redirect
// returns to exactly this page, so a load of any other path with the marker
// still set is the student coming back their own way. A path only: the
// query can carry a claim token, and nothing here needs it.
function currentPath() {
    try { return window.location.pathname || null; } catch { return null; }
}

export function markSignInPending(flow, now = Date.now(), provider = 'google') {
    try {
        sessionStorage.setItem(PENDING_KEY, JSON.stringify({ flow, t: now, page: PAGE_LIFE, path: currentPath(), provider }));
    } catch { /* storage off */ }
}

const PROVIDER_IDS = ['google', 'apple', 'facebook'];
// The names the copy uses, by provider id. Kept here rather than read from
// signInProviders.js, which imports this module.
const PROVIDER_NAMES = { google: 'Google', apple: 'Apple', facebook: 'Facebook' };

/** The provider the in-flight sign-in went to (#5154), without clearing the
 *  marker; 'google' when none is recorded (every marker before #5154). */
export function pendingProvider() {
    try {
        const p = JSON.parse(sessionStorage.getItem(PENDING_KEY) || 'null')?.provider;
        return PROVIDER_IDS.includes(p) ? p : 'google';
    } catch { return 'google'; }
}

export function clearSignInPending() {
    try { sessionStorage.removeItem(PENDING_KEY); } catch { /* storage off */ }
}

/** The sign-in a PREVIOUS page life left unfinished: {flow: 'popup' |
 *  'redirect', waitedMs, path}, or null. `path`: the page it started on
 *  (null on a marker written before #4117). Clears it. A marker from this page life
 *  is left alone and reads as null: that sign-in is still running.
 *  `restored`: this page came back from the back/forward cache (#3894). Its
 *  own redirect marker then means the student left for Google and came Back
 *  without reloading, so it is read; a popup marker still is not (the
 *  window may be open). */
export function takeLostSignIn(now = Date.now(), { restored = false } = {}) {
    let raw = null;
    try { raw = sessionStorage.getItem(PENDING_KEY); } catch { return null; }
    if (!raw) return null;
    let parsed = null;
    try { parsed = JSON.parse(raw); } catch { /* unreadable: cleared below */ }
    if (parsed?.page === PAGE_LIFE && !(restored && parsed?.flow === 'redirect')) return null;
    clearSignInPending();
    const t = parsed?.t;
    if (typeof t !== 'number' || now - t > PENDING_TTL_MS || now < t) return null;
    const path = typeof parsed.path === 'string' ? parsed.path : null;
    const provider = PROVIDER_IDS.includes(parsed.provider) ? parsed.provider : 'google';
    return { flow: parsed.flow === 'redirect' ? 'redirect' : 'popup', waitedMs: now - t, path, provider };
}

/** The path this DOCUMENT was loaded at (the navigation entry, before any
 *  in-app route change), or null when the browser cannot say. */
export function loadedPath() {
    try {
        const name = performance.getEntriesByType?.('navigation')?.[0]?.name;
        return name ? new URL(name).pathname : null;
    } catch {
        return null;
    }
}

/** How this document was loaded ('reload', 'back_forward', 'navigate'), or
 *  'unknown'. Says how a page that orphaned a sign-in was left: the student
 *  reloading reads differently from the student going Back. */
export function pageLoadType() {
    try {
        const entry = performance.getEntriesByType?.('navigation')?.[0];
        const type = entry?.type;
        return ['reload', 'back_forward', 'navigate', 'prerender'].includes(type) ? type : 'unknown';
    } catch {
        return 'unknown';
    }
}

// ── what the student reads ──────────────────────────────────────────────────
// Say what happened, then what to do (interface-copy). No Firebase codes, no
// cause we have not established. `action` names the one button the notice
// carries: 'retry' starts the sign-in again, 'copy_link' copies this page's
// address so it can be opened in a real browser.
export function signInTroubleCopy(error) {
    // A lost sign-in carries its own reason (popup vs redirect); every
    // Firebase error is classified by its code.
    const reason = Object.values(REASONS).includes(error?.reason)
        ? error.reason : failureReason(error?.code, error);
    // The provider the sign-in went to (#5154); Google for every error
    // before it, and for one that does not say.
    const name = PROVIDER_NAMES[error?.provider] || 'Google';
    switch (reason) {
        case REASONS.IN_APP_BROWSER: {
            const app = error?.app || 'this';
            return {
                reason, action: 'copy_link', actionLabel: 'Copy link',
                title: 'Open Stratia in your browser to sign in',
                message: `Google sign-in doesn't work inside the ${app} app. Tap the three dots and choose Open in browser, or copy the link and paste it into Safari or Chrome. You can also tap sign-in again to try here anyway.`,
            };
        }
        case REASONS.NETWORK:
            return {
                reason, action: 'retry', actionLabel: 'Try again',
                title: "Sign-in didn't go through",
                message: `We couldn't reach ${name}. Check your connection, then try again.`,
            };
        case REASONS.GOOGLE_UNREACHABLE:
            return {
                reason, action: 'retry', actionLabel: 'Try again',
                title: 'Google sign-in is blocked here',
                message: "This browser or network couldn't reach Google's sign-in. School and work networks and some privacy extensions block it. Try another network or browser, then try again.",
            };
        case REASONS.POPUP_BLOCKED:
            return {
                reason, action: 'retry', actionLabel: 'Try again',
                title: `${name}'s sign-in window was blocked`,
                message: 'Allow pop-ups for stratiaadmissions.com, then try again.',
            };
        case REASONS.POPUP_LOST:
            return {
                reason, action: 'retry', actionLabel: 'Try again',
                title: "Your sign-in didn't finish",
                message: `This page was reloaded or left while ${name}'s sign-in window was open, so that window can't finish signing you in. Close it if it's still open, then try again.`,
            };
        case REASONS.REDIRECT_LOST:
            return {
                reason, action: 'retry', actionLabel: 'Try again',
                title: "Your sign-in didn't finish",
                message: `You came back from ${name} without being signed in. Try again, and if it keeps happening, try another browser.`,
            };
        case REASONS.BROWSER_UNSUPPORTED:
            return {
                reason, action: 'retry', actionLabel: 'Try again',
                title: `${name} sign-in couldn't finish`,
                message: `${name}'s sign-in window couldn't open in this browser. Try again, or use a different browser.`,
            };
        case REASONS.NO_EMAIL:
            return {
                reason, action: 'retry', actionLabel: 'Use another way',
                title: "Facebook didn't give us an email",
                message: GUARD_NO_EMAIL_MESSAGE,
            };
        case REASONS.ADDRESS_MISMATCH:
            return {
                reason, action: 'retry', actionLabel: 'Try again',
                title: `This ${name} account isn't the one on your Stratia account`,
                message: `Its email is different from your account's. Sign in with a link sent to your account's email instead.`,
            };
        default:
            return {
                reason, action: 'retry', actionLabel: 'Try again',
                title: `${name} sign-in couldn't finish`,
                message: 'Please try again. If it keeps failing, try another browser.',
            };
    }
}
